import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import api, { API_BASE_URL } from "../../config/api";

const formatReminderDate = (value) => {
  if (!value) return "No date";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "No date" : parsed.toLocaleDateString();
};

const allowedTypes = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/jpg",
];

const UnbilledReminderModal = ({
  isOpen,
  onClose,
  shipments = [],
  user,
  title = "Unbilled Shipments",
  onUploaded,
}) => {
  const [localShipments, setLocalShipments] = useState(shipments);
  const [selectedId, setSelectedId] = useState("");
  const [billingDescription, setBillingDescription] = useState("");
  const [billingFiles, setBillingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLocalShipments(shipments);
  }, [shipments]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId((current) => current || String(shipments[0]?.id || ""));
    setBillingDescription("");
    setBillingFiles([]);
    setSearch("");
  }, [isOpen, shipments]);

  const filteredShipments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return localShipments;
    return localShipments.filter((shipment) =>
      [shipment.title, shipment.subtitle, shipment.meta]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [localShipments, search]);

  useEffect(() => {
    if (!filteredShipments.some((shipment) => String(shipment.id) === String(selectedId))) {
      setSelectedId(String(filteredShipments[0]?.id || ""));
    }
  }, [filteredShipments, selectedId]);

  const selectedShipment =
    filteredShipments.find((shipment) => String(shipment.id) === String(selectedId)) ||
    localShipments.find((shipment) => String(shipment.id) === String(selectedId)) ||
    null;

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      setBillingFiles([]);
      return;
    }

    const invalidTypeFile = selectedFiles.find((file) => !allowedTypes.includes(file.type));
    if (invalidTypeFile) {
      toast.error(`"${invalidTypeFile.name}" is not a supported file type`);
      event.target.value = "";
      setBillingFiles([]);
      return;
    }

    const oversizedFile = selectedFiles.find((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFile) {
      toast.error(`"${oversizedFile.name}" exceeds the 10MB file size limit`);
      event.target.value = "";
      setBillingFiles([]);
      return;
    }

    setBillingFiles((current) => {
      const mergedFiles = [...current];
      selectedFiles.forEach((file) => {
        const exists = mergedFiles.some(
          (currentFile) =>
            currentFile.name === file.name &&
            currentFile.size === file.size &&
            currentFile.lastModified === file.lastModified
        );
        if (!exists) {
          mergedFiles.push(file);
        }
      });
      return mergedFiles;
    });
    event.target.value = "";
  };

  const handleUpload = async () => {
    if (!selectedShipment?.id) {
      toast.error("Select a shipment first");
      return;
    }

    if (!billingFiles.length) {
      toast.error("Please select at least one billing file");
      return;
    }

    setUploading(true);
    try {
      for (const billingFile of billingFiles) {
        const formData = new FormData();
        formData.append("file", billingFile);
        formData.append("shipment_id", selectedShipment.id);
        formData.append("uploaded_by", user?.user_id || user?.id || "");
        formData.append(
          "uploaded_by_name",
          user?.user_name ||
            [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
            user?.email ||
            ""
        );
        formData.append("description", billingDescription);

        await api.post(`${API_BASE_URL}/api/shipment-billing/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
      }

      toast.success(
        billingFiles.length === 1
          ? "Billing document uploaded successfully"
          : `${billingFiles.length} billing documents uploaded successfully`
      );
      setLocalShipments((current) =>
        current.filter((shipment) => String(shipment.id) !== String(selectedShipment.id))
      );
      setBillingDescription("");
      setBillingFiles([]);
      const input = document.getElementById("unbilled-reminder-billing-file");
      if (input) input.value = "";
      if (onUploaded) {
        onUploaded(selectedShipment.id);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to upload billing document(s)"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (targetFile) => {
    setBillingFiles((current) =>
      current.filter(
        (file) =>
          !(
            file.name === targetFile.name &&
            file.size === targetFile.size &&
            file.lastModified === targetFile.lastModified
          )
      )
    );
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
              Bulletin Board
            </p>
            <h2 className="text-2xl font-bold text-blue">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="grid max-h-[calc(90vh-88px)] grid-cols-1 overflow-hidden lg:grid-cols-[1.05fr_0.95fr]">
          <div className="border-r border-slate-100 p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tracking no, JO, status"
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-300"
              />
              <div className="shrink-0 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                {localShipments.length}
              </div>
            </div>

            <div className="max-h-[calc(90vh-220px)] space-y-3 overflow-y-auto pr-1">
              {filteredShipments.length ? (
                filteredShipments.map((shipment) => {
                  const isSelected = String(shipment.id) === String(selectedId);
                  return (
                    <button
                      key={shipment.id}
                      type="button"
                      onClick={() => setSelectedId(String(shipment.id))}
                      className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                        isSelected
                          ? "border-blue bg-blue/5 shadow-sm"
                          : "border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="font-semibold text-slate-900">{shipment.title}</div>
                      <div className="mt-1 text-sm text-slate-600">{shipment.subtitle}</div>
                      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="uppercase tracking-wide">{shipment.meta}</span>
                        <span>{formatReminderDate(shipment.date)}</span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                  No unbilled shipments found.
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-50/60 p-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Upload Billing</h3>
              {selectedShipment ? (
                <>
                  <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm">
                    <div><strong>Tracking No:</strong> {selectedShipment.title}</div>
                    <div><strong>Job Order:</strong> {selectedShipment.subtitle || "—"}</div>
                    <div><strong>Status:</strong> {selectedShipment.meta || "—"}</div>
                    <div><strong>Date:</strong> {formatReminderDate(selectedShipment.date)}</div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={billingDescription}
                      onChange={(e) => setBillingDescription(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm outline-none focus:border-blue-300"
                    />
                    <input
                      id="unbilled-reminder-billing-file"
                      type="file"
                      multiple
                      accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="unbilled-reminder-billing-file"
                      className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-blue-200 bg-blue/5 px-4 py-3 text-sm font-semibold text-blue transition hover:border-blue hover:bg-blue/10"
                    >
                      <i className="fa-solid fa-paperclip" />
                      Add Document
                    </label>
                    {billingFiles.length ? (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                        <div className="mb-2 font-semibold text-slate-900">
                          Selected files ({billingFiles.length})
                        </div>
                        <div className="space-y-1">
                          {billingFiles.map((file) => (
                            <div
                              key={`${file.name}-${file.size}-${file.lastModified}`}
                              className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2"
                            >
                              <div className="truncate">{file.name}</div>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(file)}
                                className="shrink-0 text-xs font-semibold text-red-600 transition hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <p className="text-xs text-slate-500">
                      Allowed: PDF, Excel, CSV, Images. You can select multiple files. Maximum
                      file size: 10MB each.
                    </p>
                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={uploading || !billingFiles.length}
                      className={`w-full rounded-xl px-4 py-3 text-sm font-semibold text-white ${
                        uploading || !billingFiles.length
                          ? "cursor-not-allowed bg-slate-300"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                    >
                      {uploading ? "Uploading..." : "Upload Billing"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="mt-6 rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                  Select a shipment from the list to upload its billing document.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UnbilledReminderModal;
